import { Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import {
  AuthMode,
  Settings,
  SettingsOriginator,
  SettingsService
} from '../../../../services/settings/settings.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { MatCardAvatar } from '@angular/material/card';
import { SearchbarComponent } from '../../../../common/components/searchbar/searchbar.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from '../../../../services/dialog/dialog.service';
import { DialogData } from '../../../../common/components/dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { SettingsSliderComponent } from '../../../../common/components/base-settings-dialog/settings-slider/settings-slider.component';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { TooltipIconComponent } from '../../../../common/components/base-settings-dialog/tooltip-icon/tooltip-icon.component';
import { MatrixService } from '../../../../services/matrix/matrix.service';
import { AuthorizationManagementService } from '../../../../services/authorization-management/authorization-management.service';
import { AuthorizationList } from '../../../../../shared-api/authorizationList';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { GermanPaginatorIntl } from '../../../../services/paginator/german-paginator-intl';
import { AuthorizationMode } from '../../../../../shared-api/authorizationMode';
import { PrivacySettingsTestIds } from './privacy-settings.test-ids';
import { PresenceService } from '../../../../services/presence/presence.service';
import { MXID_PATTERN } from 'matrix-js-sdk';

import { MATRIX_SERVER_NAME_REGEX } from '../../../../services/const';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { preventInvalidNumberInput } from '../../../../services/util';

@Component({
  selector: 'app-privacysettings',
  imports: [
    FormsModule,
    MatIcon,
    ReactiveFormsModule,
    ButtonComponent,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatRow,
    MatRowDef,
    MatTable,
    MatTooltip,
    MatTabGroup,
    MatTab,
    MatFooterCellDef,
    MatHeaderCellDef,
    MatCardAvatar,
    SearchbarComponent,
    SettingsSliderComponent,
    TooltipIconComponent,
    MatPaginator,
    MatSelect,
    MatOption,
    MatFormField
  ],
  standalone: true,
  providers: [{ provide: MatPaginatorIntl, useClass: GermanPaginatorIntl }],
  templateUrl: './privacy-settings.component.html',
  styleUrl: './privacy-settings.component.scss'
})
/**
 * Die Privatseinstellungen für den User. Hierbei handelt es sich um eine überarbeitete Version der alten
 * PermissionSettings.
 *
 * Hierdurch erfüllt AFO's
 *
 * A_26016 - Basiseinstellung vorgeben
 *
 * A_25045-02 - Funktionsumfang der Berechtigungskonfiguration
 *
 * A_25433-01 - Konfiguration von Public Read Receipts
 *
 * A_26220 - Defaulteinstellung für Public Read Receipts
 *
 * A_26221 - Private Read Receipts
 *
 * A_25431 - Eingabebenachrichtigungen
 *
 * A_25043 - Berechtigungskonfiguration in Accountdaten speichern
 *
 * A_26016 - Basiseinstellung vorgeben
 */
export class PrivacySettingsComponent implements OnInit {
  title = signal<string>('User');
  @ViewChild('userTable') userTable: MatTable<object> | undefined;
  @ViewChild('serverTable') serverTable: MatTable<object> | undefined;
  @ViewChild('groupTable') groupTable: MatTable<object> | undefined;
  @ViewChild('userPaginator') userPaginator!: MatPaginator;
  @ViewChild('groupPaginator') groupPaginator!: MatPaginator;
  @ViewChild('serverPaginator') serverPaginator!: MatPaginator;
  // Authorization mode
  authMode = AuthMode.ALLOW_ALL;
  users: string[] = [];
  groups: string[] = [];
  servers: string[] = [];
  settingsOriginator: SettingsOriginator = new SettingsOriginator(Settings.getInstance());
  berechtigungsmanagementVisible = false;
  conversionFactor = 3600000;
  maxInactivity = 168;
  infoTextPresence = 'Ihr Status wird verborgen. Ihrem Gegenüber werden Sie als offline angezeigt';
  infoTextInacitvity = 'Die Inaktivität muss in Stunden angegeben werden';
  displayedColumns = ['avatar', 'timAdress', 'remove'];
  selected!: string | undefined;
  pageSize = 3;
  currentPage = 0;
  isInsuredPersonMap: Record<string, string> = {
    isInsuredPerson: 'Versicherte'
  };
  protected readonly testIds = PrivacySettingsTestIds;
  protected readonly matDialog = inject(MatDialog);
  protected readonly dialogService = inject(DialogService);
  protected readonly snackbarService = inject(SnackbarService);
  protected readonly Object = Object;
  protected readonly AuthMode = AuthMode;
  private readonly matrix = inject(MatrixService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly settingsService = inject(SettingsService);
  private readonly fb = inject(FormBuilder);
  readonly userForm = this.fb.group({
    name: ['', [Validators.required, Validators.pattern(MXID_PATTERN)]]
  });
  readonly groupForm = this.fb.group({
    name: ['', Validators.required]
  });
  readonly serverForm = this.fb.group({
    name: ['', [Validators.required, Validators.pattern(MATRIX_SERVER_NAME_REGEX)]]
  });
  private readonly presenceService = inject(PresenceService);
  private readonly authorizationManagement = inject(AuthorizationManagementService);

  /**
   * Rechnet die in den Settings vorhandenen Millisekunden in Stunden um
   */
  get inactivityInHours(): number {
    return this.settingsOriginator.state.settingsConfigurations.inactivity / this.conversionFactor;
  }

  /**
   * Rechnet die eingegebenen Stunden in Millisekunden um
   * @param value
   */
  set inactivityInHours(value: number) {
    this.settingsOriginator.state.settingsConfigurations.inactivity = value * this.conversionFactor;
  }

  get activeForm(): FormGroup {
    switch (this.title()) {
      case 'User':
        return this.userForm;
      case 'Gruppe':
        return this.groupForm;
      case 'Server':
        return this.serverForm;
      default:
        throw new Error('Unbekannter Dialog-Typ');
    }
  }

  ngOnInit(): void {
    this.authorizationManagement.fetchConfig();
    this.authorizationManagement.authMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async authMode => {
      this.authMode = authMode;
      await this.loadAuthorizationList();
    });
  }

  async saveSettings(): Promise<void> {
    try {
      if (this.inactivityInHours > this.maxInactivity) {
        this.snackbarService.error('Bitte geben Sie eine Zahl zwischen 1 und 168 ein');
        return;
      }
      // Save Settings except for Permission Settings
      await this.settingsService.saveSettings(this.matrix.client, this.settingsOriginator.state);
      await this.presenceService.updateCurrentUserPresence();
      // add permissions from datasources
      const authList: AuthorizationList = {
        mxids: this.users.map(user => ({ mxid: user })),
        serverNames: this.servers.map(server => ({ serverName: server })),
        groupNames: this.groups
      };
      if (this.authMode === AuthMode.ALLOW_ALL) {
        await this.authorizationManagement.addBlockedUsersList(authList);
      }
      if (this.authMode === AuthMode.BLOCK_ALL) {
        await this.authorizationManagement.addAllowedUsersList(authList);
      }
      this.snackbarService.success('Erfolgreich gespeichert');
    } catch (e) {
      console.error('exception', e);
      this.snackbarService.error('Fehler beim Speichern der Privatsphäre-Einstellungen');
    }
  }

  deleteItemFromUserList(selectedUserId: string): void {
    const authList: AuthorizationList = {
      mxids: [{ mxid: selectedUserId }],
      serverNames: [],
      groupNames: []
    };

    if (this.authMode === AuthMode.ALLOW_ALL) {
      this.authorizationManagement.deleteBlockedUsersList(authList).catch(error => {
        console.error('Delete user failed:', error);
      });
    } else {
      this.authorizationManagement.deleteAllowedUsersList(authList).catch(error => {
        console.error('Delete user failed:', error);
      });
    }
    this.users = this.users.filter(userId => userId !== selectedUserId);
    this.userTable?.renderRows();

    // Wenn auf aktueller Seite kein Eintrag mehr, dann zurückblättern
    if (this.currentPage * this.pageSize >= this.users.length && this.currentPage > 0) {
      this.currentPage--;
      this.userPaginator.pageIndex = this.currentPage;
    }
  }

  deleteItemFromGroupList(groupName: string): void {
    const authList: AuthorizationList = {
      mxids: [],
      serverNames: [],
      groupNames: [groupName]
    };

    if (this.authMode === AuthMode.ALLOW_ALL) {
      this.authorizationManagement.deleteBlockedUsersList(authList).catch(error => {
        console.error('Delete group failed:', error);
      });
    } else {
      this.authorizationManagement.deleteAllowedUsersList(authList).catch(error => {
        console.error('Delete group failed:', error);
      });
    }
    this.groups = this.groups.filter(it => it != groupName);
    this.groupTable?.renderRows();

    // Wenn auf aktueller Seite kein Eintrag mehr, dann zurückblättern
    if (this.currentPage * this.pageSize >= this.groups.length && this.currentPage > 0) {
      this.currentPage--;
      this.groupPaginator.pageIndex = this.currentPage;
    }
  }

  deleteItemFromServerList(selectedServerId: string): void {
    const authList: AuthorizationList = {
      mxids: [],
      serverNames: [{ serverName: selectedServerId }],
      groupNames: []
    };

    if (this.authMode === AuthMode.ALLOW_ALL) {
      this.authorizationManagement.deleteBlockedUsersList(authList).catch(error => {
        console.error('Delete server failed:', error);
      });
    } else {
      this.authorizationManagement.deleteAllowedUsersList(authList).catch(error => {
        console.error('Delete server failed:', error);
      });
    }
    this.servers = this.servers.filter(serverId => serverId !== selectedServerId);
    this.serverTable?.renderRows();

    // Wenn auf aktueller Seite kein Eintrag mehr, dann zurückblättern
    if (this.currentPage * this.pageSize >= this.servers.length && this.currentPage > 0) {
      this.currentPage--;
      this.serverPaginator.pageIndex = this.currentPage;
    }
  }

  async select(selectedAuthMode: AuthMode): Promise<void> {
    switch (selectedAuthMode) {
      case AuthMode.ALLOW_ALL:
        await this.authorizationManagement.setAuthorizationMode(AuthorizationMode.AllowAll);
        this.authMode = selectedAuthMode;
        this.resetData();
        break;
      case AuthMode.BLOCK_ALL:
        this.openDenyWarnDialog().subscribe(resp => {
          if (resp) {
            this.authorizationManagement.setAuthorizationMode(AuthorizationMode.BlockAll);
            this.authMode = selectedAuthMode;
            this.resetData();
          }
        });
        break;
    }
  }

  toggleDropdown(): void {
    this.berechtigungsmanagementVisible = !this.berechtigungsmanagementVisible;
  }

  resetForm(): void {
    this.activeForm.reset({ name: '' });
    this.matDialog.closeAll();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.title.set(event.tab.textLabel);
  }

  handlePageEvent(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  modifyDatasourceForPage(array: string[]): string[] {
    const startIndex = this.currentPage * this.pageSize;
    return array.slice(startIndex, startIndex + this.pageSize);
  }

  /**
   * Fügt User/Server/Gruppe hinzu
   */
  protected addEntry(title: string, name: string | null | undefined): void {
    if (!name) {
      throw new Error('addException: Nothing to add');
    }
    if (this.activeForm.invalid) {
      throw new Error(`addException: Form not valid`);
    }
    switch (title) {
      case 'User': {
        this.users.push(name);
        break;
      }
      case 'Server': {
        this.servers.push(name);
        break;
      }
      case 'Gruppe': {
        this.groups.push(name);
        break;
      }
    }
    this.renderTables();
    this.resetForm();
    this.matDialog.closeAll();
  }

  private async loadAuthorizationList(): Promise<void> {
    try {
      let list: AuthorizationList;

      if (this.authMode === AuthMode.ALLOW_ALL) {
        list = await this.authorizationManagement.getBlockedUsersList();
      } else {
        list = await this.authorizationManagement.getAllowedUsersList();
      }

      this.users = list.mxids.map(x => x.mxid);
      this.servers = list.serverNames.map(x => x.serverName);
      this.groups = list.groupNames;
    } catch (e) {
      console.error('Fehler beim Laden der Liste', e);
      this.users = [];
      this.servers = [];
      this.groups = [];
    }
  }

  /**
   * Re-initialisiere users, groups, servers
   */
  private resetData(): void {
    this.users = [];
    this.servers = [];
    this.groups = [];
    this.renderTables();
  }

  private renderTables(): void {
    this.userTable?.renderRows();
    this.groupTable?.renderRows();
    this.serverTable?.renderRows();
  }

  private openDenyWarnDialog(): Observable<boolean> {
    const warnDialogData: DialogData = {
      title: 'SICHER UMSCHALTEN?',
      svgIcon: 'circle-warning',
      message:
        'Durch Umschalten kann Sie grundsätzlich niemand kontaktieren. ' +
        'Dadurch können Sie eine neue Liste zur Freigabe von Accounts pflegen. ' +
        'Beim Umschalten gehen keine Konfigurationen verloren. Wollen Sie die Liste an Berechtigungen umschalten?',
      confirmText: 'Ja',
      cancelText: 'Nein',
      cancelDataTestId: 'button_cancel_permission',
      confirmDataTestId: 'button_confirm_permission',
      dialogActionTestId: 'dialog_permission_actions',
      dialogContentTestId: 'dialog_permission_content'
    };

    return this.dialogService.openWarningDialog(warnDialogData).pipe(takeUntilDestroyed(this.destroyRef));
  }

  protected readonly preventInvalidNumberInput = preventInvalidNumberInput;
}
